import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { update } from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import { OrderRow } from '../../Types/OrderRow';
import QuantityInput from '../QuantityInput/QuantityInput';
import StockStatus from '../StockStatus/StockStatus';
import clsx from 'clsx';
import Icon from '../Icon';

interface OrderRowProductCardProps {
  row: OrderRow;
  onRemoveClick?: (row: OrderRow) => void;
  className?: string;
  priceToggleType?: string;
}

const OrderRowProductCard = ({
  row,
  onRemoveClick,
  className = '',
  priceToggleType = '',
}: OrderRowProductCardProps) => {
  const dispatch = useDispatch();

  const [abortController, setAbortController] = useState<
    AbortController | undefined
  >();

  useEffect(() => setAbortController(new AbortController()), []);

  const onQuantityChange = useCallback(
    (row: OrderRow, quantity: number) => {
      dispatch(
        update(
          row.articleNumber,
          quantity,
          row.rowSystemId,
          abortController
        )
      );
    },
    [abortController, dispatch]
  );

  return (
    <div
      className={clsx(
        className,
        'flex gap-x-4 rounded border border-solid border-gray-200 p-4',
        row.quantityExceeded ? 'bg-gray-100' : ''
      )}
    >
      <div className="h-16 w-16 bg-gray-200">
        {row.image && (
          <a href={row.url}>
            <img
              className="aspect-square w-full object-cover"
              src={row.image}
            />
          </a>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-col md:flex-row md:justify-between md:gap-4">
          <div className="flex flex-col gap-1">
            {row.brand && (
              <div className="text-md text-gray-600">{row.brand}</div>
            )}
            <div className="text-lg font-bold">
              <a href={row.url}>{row.name}</a>
            </div>
            {row.articleNumber && (
              <p className={clsx('text-md', 'text-gray-600')}>
                {translate('product.articleno')} {row.articleNumber}
              </p>
            )}
            {row.itemBIDPromotionCode && (
              <p className={clsx('text-md', 'text-gray-600')}>
                {translate('product.item-bid')}: {row.itemBIDPromotionCode}
              </p>
            )}
            {row.extraInfo && (
              <div>
                {row.extraInfo.items.map((item) => (
                  <div
                    className="text-md text-gray-600"
                    key={'mini-cart' + item.key}
                  >
                    {item.key}: {item.value}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            {row.totalCampaignPrice ? (
              <div className="flex flex-col items-end">
                <span className="text-md font-bold opacity-50">
                  {row.campaignPrice}/st
                </span>
                <p className="text-lg font-bold text-error-500 md:text-right">
                  <span>{row.totalCampaignPrice}</span>
                </p>
                <p
                  className={clsx(
                    'line-through',
                    'text-sm',
                    'text-gray-600',
                    'md:text-right'
                  )}
                >
                  {row.totalPrice}
                </p>
              </div>
            ) : (
              row.totalPrice && (
                <div className="flex flex-col items-end">
                  <span className="text-md font-bold opacity-50">
                    {row.price}/st
                  </span>
                  <p className="flex text-lg font-bold md:text-right">
                    <span>{row.totalPrice}</span>
                  </p>
                </div>
              )
            )}
          </div>
        </div>
        {row.productFee &&
          row.productFee.length > 0 &&
          row.productFee.map((fee) => (
            <p
              className={clsx('text-sm', 'text-gray-500')}
              key={fee.feeTypeName}
            >
              {translate('product.fee')} ({fee.feeTypeName}){': '}
              {fee.priceModel.formattedPrice}
            </p>
          ))}

        {priceToggleType?.length > 0 && row.showNavStock ? (
          row.navisionStock && row.navisionStock.stockStatuses ? (
            <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
              {row.navisionStock?.stockStatuses[0] && (
                <StockStatus
                  {...row.navisionStock?.stockStatuses[0].stockStatus}
                />
              )}
              {row.navisionStock?.stockStatuses[1] && (
                <StockStatus
                  {...row.navisionStock?.stockStatuses[1].stockStatus}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
              <StockStatus {...row.stockStatusPlaceholder} />{' '}
              <StockStatus {...row.stockStatusPlaceholder} />
            </div>
          )
        ) : (
          <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
            <StockStatus {...row.stockStatus} className="mb-4 mt-2" />
          </div>
        )}

        {row.orderRowInfoMessage && (
          <div className="flex max-w-fit flex-row items-center gap-2 rounded border border-solid border-warning-600 bg-white px-3 py-2">
            <Icon
              className="h-5 w-5 shrink-0 text-warning-600"
              name="warning"
            />
            <span className="flex text-md text-warning-600">
              {row.orderRowInfoMessage}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <QuantityInput
            packagingSize={row.packagingSize}
            quantity={
              row.notDivisible
                ? row.quantity / row.packagingSize
                : row.quantity
            }
            showButtons={!row.isFreeGift}
            disabled={row.isFreeGift}
            wrapperClassName="max-w-[200px]"
            onChange={(quantity) => onQuantityChange(row, quantity)}
          />
          {onRemoveClick && !row.isFreeGift && (
            <button
              className="color-gray-500 cursor-pointer text-md underline"
              onClick={() => onRemoveClick && onRemoveClick(row)}
            >
              {translate('general.remove')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderRowProductCard;
